import React, {useEffect, useRef} from "react";

/**
 * Wraps a child element with the IntersectionObserver-based "reveal on scroll"
 * effect used across EAS pages — fade + 24px slide up the first time the element
 * enters the viewport.
 *
 * Implementation note: we add the .eas-reveal class on mount (server-rendered
 * markup is already styled), then toggle .in via IO. Once revealed we unobserve.
 */
export default function EasReveal({
    children,
    className = "",
    style,
    threshold = 0.1,
}: {
    children: React.ReactNode
    className?: string
    style?: React.CSSProperties
    threshold?: number
}): JSX.Element {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver((entries) => {
            entries.forEach((e) => {
                if (e.isIntersecting) {
                    el.classList.add("in");
                    io.unobserve(el);
                }
            });
        }, {threshold});
        io.observe(el);
        return () => io.disconnect();
    }, [threshold]);

    return (
        <div ref={ref} className={`eas-reveal ${className}`} style={style}>
            {children}
        </div>
    );
}
